import { useEffect, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { CustomButtonPlusIcon } from "../Design/CustomButtonPlusIcon";
import {
  dateFormat,
  handleDates,
  quitarTildes,
  statusConfig,
} from "../../helpers/Helpers";

import { AccessTime, Gavel, Visibility, Warning } from "@mui/icons-material";

// Componentes propios
import { CustomIconsPlusText } from "../Design/CustomIconsPlusText";

// Paginación
import dayjs from "dayjs";

import "dayjs/locale/es"; // Importa español
import relativeTime from "dayjs/plugin/relativeTime";
import { Box } from "@mui/system";
dayjs.extend(relativeTime);
dayjs.locale("es");

export const RenderCell = ({
  value,
  defaultValue = "Sin asignar",
  link = false,
  dateFlag = false,
  icon: Icon,
  sx,
  id,
  view,
  color,
  iconPosition,
}) => {
  const [displayValue, setDisplayValue] = useState("");

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("md")); // true si md o menos

  const isValidValue =
    value != null &&
    value !== "" &&
    value !== "undefined" &&
    !String(value).includes("undefined");

  useEffect(() => {
    const isDate = (value) => {
      if (!value) return false;

      // Solo strings con el formato correcto
      if (typeof value === "string") {
        return dayjs(value, "YYYY-MM-DD HH:mm:ss", true).isValid();
      }

      return false;
    };

    if (isValidValue) {
      if (dateFlag && isDate(value)) {
        setDisplayValue(dateFormat(value));
      } else {
        setDisplayValue(value);
      }
    } else {
      setDisplayValue(defaultValue);
    }
  }, [dateFlag, defaultValue, isValidValue, value]);

  return (
    <div className="centerContent">
      {link && isValidValue ? (
        <CustomButtonPlusIcon
          sx={sx}
          to={`/${view}/ver?id=${id}`}
          icon={Icon}
          text={isMd ? "" : displayValue}
          size="small"
          position={iconPosition || "start"}
          color={color}
          variant="outlined"
          fullWidth={true}
        />
      ) : (
        displayValue
      )}
    </div>
  );
};

export const RenderVencimiento = ({
  last_task: current_task,
  cases_flow_relation: flow,
  flagFromPanel = false,
  flagTrial = false,
  full_case_status_name,
  status_name,
}) => {
  const fechaFormateada = dateFormat(current_task?.date_end);
  const iconSize = { width: "15px", height: "15px" };

  const statusLabel = quitarTildes(
    `${full_case_status_name || status_name || ""}`.toLowerCase(),
  );
  const taskIsActive =
    current_task?.status === 0 ||
    current_task?.status === "0" ||
    (!current_task?.status &&
      !/(cancel|cerrad|caso cerrado|finaliz|conclu)/.test(statusLabel));
  const isInactiveCaseState =
    /(cancel|cerrad|caso cerrado|finaliz|conclu)/.test(statusLabel);
  const shouldDisplayDeadline = taskIsActive && !isInactiveCaseState;

  // Calcular diferencia en días con dayjs
  const diffDays = current_task?.date_end
    ? dayjs(current_task.date_end).diff(dayjs(), "day")
    : null;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: flagFromPanel ? "row" : "column",
        gap: "3px",
        padding: "2px",
      }}
    >
      {shouldDisplayDeadline && diffDays !== null && (
        <>
          {diffDays > 5 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={AccessTime}
              iconStyles={iconSize}
              severity="info"
              text={`${diffDays} días para finalizar la tarea`}
              variant={"outlined"}
            />
          )}

          {diffDays > 0 && diffDays <= 5 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Warning}
              iconStyles={iconSize}
              blink="blinking"
              severity="warning"
              text={`${diffDays} días para finalizar la tarea`}
              variant={"outlined"}
            />
          )}

          {diffDays <= 0 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Warning}
              iconStyles={iconSize}
              blink="blinking"
              severity="error"
              text={`Tarea vencida hace ${Math.abs(diffDays)} días`}
              variant={"outlined"}
            />
          )}
        </>
      )}

      {/* Fecha de vencimiento */}
      <CustomIconsPlusText
        chipStyles={{ width: "auto" }}
        icon={AccessTime}
        iconStyles={iconSize}
        severity={shouldDisplayDeadline ? "none" : "default"}
        text={
          shouldDisplayDeadline
            ? `Fecha: ${fechaFormateada ? fechaFormateada : "Sin programar"}`
            : `Fecha: ${fechaFormateada ? fechaFormateada : "Sin programar"} (inactivo)`
        }
        variant={"outlined"}
      />
    </div>
  );
};

export const RenderTrialDate = ({
  dates,
  cases_flow_relation: flow,
  flagFromPanel = false,
  flagTrial = false,
}) => {
  const date = dates?.date_end ? dayjs(dates.date_end) : null;
  const iconSize = { width: "15px", height: "15px" };
  const now = dayjs();

  const diffDays = date ? date.diff(now, "day") : null;
  const diffHours = date ? date.diff(now, "hour") % 24 : null;

  // Formato español con am/pm
  const formattedDate = date
    ? date.format("DD [de] MMMM [de] YYYY hh:mm A") // Ej: 12 de febrero de 2026 02:30 PM
    : "";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: flagFromPanel ? "row" : "column",
        gap: "3px",
        padding: "2px",
      }}
    >
      {diffDays !== null && (
        <>
          {diffDays > 15 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Gavel}
              iconStyles={iconSize}
              text={
                dates?.source === "task_date"
                  ? "JUICIO POR VENCIMIENTO DE TAREA"
                  : "JUICIO FECHA REAL PROGRAMADA"
              }
              blink="blinking"
              severity="warning"
              variant="contained"
            />
          )}

          {diffDays > 0 && diffDays <= 15 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Gavel}
              iconStyles={iconSize}
              text={
                dates?.source === "task_date"
                  ? "JUICIO POR VENCIMIENTO DE TAREA"
                  : "JUICIO FECHA REAL PROGRAMADA"
              }
              blink="blinking"
              severity="error"
              variant="contained"
            />
          )}

          {diffDays > 5 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={AccessTime}
              iconStyles={iconSize}
              severity="info"
              text={`Faltan ${diffDays} días ${diffHours} horas`}
              variant="outlined"
            />
          )}

          {diffDays > 0 && diffDays <= 5 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Warning}
              iconStyles={iconSize}
              blink="blinking"
              severity="warning"
              text={`Faltan ${diffDays} días ${diffHours} horas`}
              variant="outlined"
            />
          )}

          {diffDays <= 0 && (
            <CustomIconsPlusText
              chipStyles={{ width: "auto" }}
              icon={Warning}
              iconStyles={iconSize}
              blink="blinking"
              severity="error"
              text={`Tarea vencida hace ${Math.abs(diffDays)} días`}
              variant="outlined"
            />
          )}
        </>
      )}

      {/* Fecha legible en español con AM/PM */}
      <CustomIconsPlusText
        chipStyles={{ width: "auto" }}
        icon={AccessTime}
        iconStyles={iconSize}
        severity={formattedDate ? "success" : "error"}
        text={`Fecha: ${formattedDate ? formattedDate : "No programada"}`}
        variant={formattedDate ? "contained" : "outlined"}
      />
    </div>
  );
};

export const RenderStatus = ({ value }) => {
  const normalized = (value || "").toLowerCase();

  const getStatusConfig = () => {
    const statusLabel = normalized.trim();

    if (/cancel/.test(statusLabel)) return statusConfig[2];
    if (/pendiente/.test(statusLabel)) return statusConfig[0];
    if (/complet|finaliz|conclu/.test(statusLabel)) return statusConfig[1];
    if (/ganado/.test(statusLabel)) return statusConfig[3];
    if (/cerrad|caso cerrado/.test(statusLabel)) return statusConfig[4];

    return {
      icon: AccessTime,
      severity: "default",
      text: () => value || "Sin estado",
      variant: "outlined",
    };
  };

  const config = getStatusConfig();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CustomIconsPlusText
        icon={config?.icon || AccessTime}
        iconStyles={{ fontSize: "14px" }}
        severity={config?.severity || "default"}
        text={`${value || "Sin estado"}`}
        variant={config?.variant || "outlined"}
      />
    </Box>
  );
};
